import { BrandUploadError, type BrandUploadResult } from "./brand-upload";

export const MAX_BRAND_PHOTOS = 12;
export const BRAND_UPLOAD_COOLDOWN_MS = 2100;
export type BatchItem = { index: number; name: string; status: "uploaded" | "invalid" | "failed" | "skipped"; reason?: "file" | "limit" | "stopped"; message?: string };
export type BatchSummary = { purpose: "logo" | "photo"; selected: number; uploaded: number; failed: number; skipped: number; stopped: boolean; items: BatchItem[] };
export type BatchPosition = { index: number; total: number; name: string; phase: "waiting" | "uploading" };

export function stopBatchOnUploadError(error: unknown) {
  // A rejected image can be skipped. Authentication, quota, network, timeout
  // and provider/service failures stop the queue; never retry paid uploads.
  return !(error instanceof BrandUploadError && [400, 413, 415, 422].includes(error.status || 0));
}

export async function runBrandMediaBatch(options: {
  files: readonly File[];
  purpose: "logo" | "photo";
  remainingPhotos: number;
  canContinue(): boolean;
  waitBeforeUpload(): Promise<void>;
  upload(file: File): Promise<BrandUploadResult["asset"]>;
  onUploaded(asset: BrandUploadResult["asset"]): void;
  onPosition(position: BatchPosition): void;
  stopOnError?(error: unknown): boolean;
}): Promise<BatchSummary> {
  const items: BatchItem[] = [];
  const queue: { file: File; index: number }[] = [];
  const capacity = options.purpose === "logo" ? 1 : Number.isFinite(options.remainingPhotos) ? Math.min(MAX_BRAND_PHOTOS, Math.max(0, Math.floor(options.remainingPhotos))) : 0;
  for (const [index, file] of options.files.entries()) {
    if (!/^image\/(jpeg|png|webp)$/.test(file.type) || file.size <= 0 || file.size > 8 * 1024 * 1024) items.push({ index, name: file.name, status: "invalid", reason: "file" });
    else if (queue.length >= capacity) items.push({ index, name: file.name, status: "skipped", reason: "limit" });
    else queue.push({ file, index });
  }
  let stopped = false;
  const stop = (position: number) => {
    stopped = true;
    for (const { file, index } of queue.slice(position)) items.push({ index, name: file.name, status: "skipped", reason: "stopped" });
  };
  for (const [position, entry] of queue.entries()) {
    if (!options.canContinue()) { stop(position); break; }
    const progress = { index: position + 1, total: queue.length, name: entry.file.name };
    try {
      options.onPosition({ ...progress, phase: "waiting" });
      await options.waitBeforeUpload();
      if (!options.canContinue()) { stop(position); break; }
      options.onPosition({ ...progress, phase: "uploading" });
      const asset = await options.upload(entry.file);
      if (!options.canContinue()) { stop(position); break; }
      options.onUploaded(asset);
      items.push({ index: entry.index, name: entry.file.name, status: "uploaded" });
    } catch (error) {
      if (!options.canContinue()) { stop(position); break; }
      items.push({ index: entry.index, name: entry.file.name, status: "failed", message: error instanceof Error ? error.message.slice(0, 1000) : undefined });
      if (options.stopOnError?.(error)) { stop(position + 1); break; }
    }
  }
  items.sort((left, right) => left.index - right.index);
  return { purpose: options.purpose, selected: options.files.length, uploaded: items.filter(item => item.status === "uploaded").length, failed: items.filter(item => item.status === "failed" || item.status === "invalid").length, skipped: items.filter(item => item.status === "skipped").length, stopped, items };
}
